/**
 * Custom error classes for API error handling.
 * Replaces @paraflow-ai/frontend-libs error types.
 */

export class HttpError extends Error {
  constructor(public status: number, public body: string) {
    super(`HTTP ${status}`)
    this.name = 'HttpError'
  }
}

export class AuthRequiredError extends Error {
  constructor() {
    super('Authentication required')
    this.name = 'AuthRequiredError'
  }
}

export class ForbiddenError extends Error {
  constructor() {
    super('Forbidden')
    this.name = 'ForbiddenError'
  }
}

export class FormError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FormError'
  }
}

/**
 * In production, API requests are sent to the Workers backend URL.
 * In development, Vite proxy handles /api → localhost:8787.
 */
const API_BASE = import.meta.env.VITE_API_BASE || ''

/**
 * Fetch wrapper that includes credentials and handles auth errors.
 * Drop-in replacement for @paraflow-ai/frontend-libs apiFetch.
 *
 * Only throws on 401 (redirect to login) and 403 (forbidden).
 * Other error statuses are returned as-is so callers can handle them.
 */
export const apiFetch: typeof fetch = async (input, init) => {
  // Prepend API_BASE to relative URLs
  let url = input
  if (typeof input === 'string' && input.startsWith('/') && API_BASE) {
    url = API_BASE + input
  } else if (input instanceof Request && input.url.startsWith('/') && API_BASE) {
    url = new Request(API_BASE + input.url, input)
  }

  const res = await fetch(url, {
    ...init,
    credentials: 'include',
  })

  if (res.status === 401) {
    throw new AuthRequiredError()
  }
  if (res.status === 403) {
    throw new ForbiddenError()
  }

  return res
}
